/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormulaContext, FormulaTestEvent } from '@gh/builder-shared';
import { Formula, isFormulaEmpty, isFormulaValid } from '@gh/builder-shared-data';
import { getRouteInitData, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { isSomeTrue } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { map$, filter$, switchMap$ } from '@gh/rx/operators';
import { CellDoubleClickedEvent } from 'ag-grid-community';
import { isEmpty } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import {
  generateHpeComponentItemId,
  getHpeComponentId,
  HpeComponent,
  HpeComponentItem,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import {
  HpeDetailsState,
  queryHpeComponentItems,
  queryHpeEditable,
  queryHpeFormulaHasTopsFromGto,
  queryHpeSelectedComponentId,
  queryHpeSelectedDetailsTab,
  queryHpeSelectionHighlightInvalid,
  querySelectedComponent,
} from '../hpe-details.state';
import { HpeDetailsComponentItemGridDefinition } from './hpe-details-component-item-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-hpe-details-component-details',
  templateUrl: 'hpe-details-component-details.component.html',
  styleUrls: ['hpe-details-component-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HpeDetailsComponentItemGridDefinition],
})
export class HpeDetailsComponentDetailsComponent implements OnInit, OnDestroy {
  items$: Observable<HpeComponentItem[]>;
  formula$: Observable<Maybe<Formula>>;
  selectionIdentity$: Observable<Maybe<ID>>;
  memo$: Observable<string>;
  total$: Observable<number>;
  selectedTab$: Observable<string>;
  formulaEditable$: Observable<boolean>;
  highlightInvalid$: Observable<boolean>;
  isFormulaNotPropagatable$: Observable<boolean>;

  editable: boolean;

  contextItem: HpeComponentItem;

  formulaContext: FormulaContext;

  componentId: ID;
  component: HpeComponent;
  private componentSubscription: Subscription;

  constructor(public route: ActivatedRoute,
              public itemGridDefinition: HpeDetailsComponentItemGridDefinition,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService) {

  }

  ngOnInit(): void {
    const { formulaContext } = getRouteInitData(this.route);

    this.formulaContext = formulaContext;

    const { state } = this;
    const componentId$ = state.select(queryHpeSelectedComponentId).pipe(filter$(Boolean));
    const component$ = state.select(querySelectedComponent).pipe(filter$(Boolean));

    const formula$ = component$.pipe(map$((component: any) => component.formula));
    this.items$ = componentId$.pipe(switchMap$((id) => state.select(queryHpeComponentItems, id)));
    this.formula$ = formula$;
    this.selectionIdentity$ = state.select(queryHpeSelectedComponentId);
    this.memo$ = component$.pipe(map$((component: any) => component.memo));
    this.total$ = component$.pipe(map$((component: any) => component.cost || 0));
    this.selectedTab$ = state.select(queryHpeSelectedDetailsTab).pipe(filter$(Boolean));
    this.formulaEditable$ = component$.pipe(map$((component: any) => isEmpty(component.cuttingList)));
    this.highlightInvalid$ = state.select(queryHpeSelectionHighlightInvalid);
    this.editable = state.project(queryHpeEditable);

    const isFormulaEmpty$ = formula$.pipe(map$(isFormulaEmpty));
    const isFormulaNotValid$ = formula$.pipe(
      map$((formula) => isFormulaEmpty(formula) ? false : !isFormulaValid(formula)));
    const isFormulaHasTopsFromGto$ = formula$.pipe(map$((formula) => isFormulaEmpty(formula)
      ? false
      : this.state.project(queryHpeFormulaHasTopsFromGto, formula)));
    this.isFormulaNotPropagatable$ = combineLatest$(
      isFormulaEmpty$,
      isFormulaNotValid$,
      isFormulaHasTopsFromGto$).pipe(map$(isSomeTrue));

    this.componentSubscription = component$.subscribe((component: any) => {
      this.component = component;
    });
  }

  ngOnDestroy(): void {
    this.componentSubscription.unsubscribe();
  }

  onTest(event: FormulaTestEvent): void {
    this.commands.testComponentFormula(event, getHpeComponentId(this.component));
  }

  onFormulaChange(formula: Formula): void {
    const componentId = getHpeComponentId(this.component);
    if (componentId) {
      this.commands.componentSetField(componentId, 'formula', formula);
    }
  }

  onMemoChange(memo: string): void {
    const componentId = getHpeComponentId(this.component);
    if (componentId) {
      this.commands.componentSetField(componentId, 'memo', memo);
    }
  }

  onSelectedTabChange(name: string): void {
    this.commands.tabSelect(name);
  }

  goToItem(item: HpeComponentItem): void {
    const componentId = getHpeComponentId(this.component);

    if (componentId && item && item.costCenterId) {
      this.commands.costCenterItemSelect(
        item.costCenterId,
        generateHpeComponentItemId(componentId, item.costCenterId, item.itemId));
    }
  }

  onItemsGridCellDoubleClicked(event: CellDoubleClickedEvent<HpeComponentItem>): void {
    if (event.data) {
      this.goToItem(event.data);
    }
  }

  onContextItemChange(item: HpeComponentItem): void {
    this.contextItem = item;
  }

  showPropagateFormulaPopup(): void {
    this.commands.propagateComponentFormula(this.component);
  }
}
